import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useSelector } from "react-redux";

import ComplaintModalLayout from "./ComplaintModalLayout";

import ComplaintTypeStep from "./steps/ComplaintTypeStep";
import ComplaintProductStep from "./steps/ComplaintProductStep";
import ComplaintDetailsStep from "./steps/ComplaintDetailsStep";
import ComplaintContactStep from "./steps/ComplaintContactStep";
import ComplaintReviewStep from "./steps/ComplaintReviewStep";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import { GetInfosContact, SubmitComplaint } from "../../../api/ComplaintApi";

function ComplaintAddModal({ open, onClose, onComplaintAdded }) {
  const { t } = useTranslation(["Complaint"]);

  // =========================================================
  // SNACKBAR
  // =========================================================

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // =========================================================
  // USER / REDUX
  // =========================================================

  const userState = useSelector((state) => state.user);

  const userLang = userState?.lang || "fr";

  const token = userState?.token;

  const userName = userState?.username;

  const idRestaurant = userState?.location?.noEmplacement;

  const noEmplacement = userState?.location?.noEmplacement;

  // =========================================================
  // NAVIGATION
  // =========================================================

  const [activeStep, setActiveStep] = useState(0);

  const [showValidation, setShowValidation] = useState(false);

  // =========================================================
  // COMPLAINT TYPE
  // =========================================================

  const [complaintType, setComplaintType] = useState("");

  // =========================================================
  // ITEM
  // =========================================================

  const [selectedItem, setSelectedItem] = useState("");

  const [selectedItemData, setSelectedItemData] = useState(null);

  // =========================================================
  // DETAILS
  // =========================================================

  const [lotNumber, setLotNumber] = useState("");

  const [productionDate, setProductionDate] = useState("");

  const [expirationDate, setExpirationDate] = useState("");

  const [quantity, setQuantity] = useState("");

  const [quantityType, setQuantityType] = useState("");

  const [description, setDescription] = useState("");

  const [files, setFiles] = useState([]);

  // =========================================================
  // CONTACT
  // =========================================================

  const [contactName, setContactName] = useState("");

  const [phoneNumber, setPhoneNumber] = useState("");

  const [deliveryDate, setDeliveryDate] = useState("");

  // =========================================================
  // SUBMITTING
  // =========================================================

  const [submitting, setSubmitting] = useState(false);

  // =========================================================
  // SNACKBAR HELPERS
  // =========================================================

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((previous) => ({
      ...previous,
      open: false,
    }));
  };

  // =========================================================
  // STEPS
  // =========================================================

  const steps = useMemo(() => {
    const result = [
      {
        id: "type",
        label: t("Type"),
      },
    ];

    if (complaintType === "product") {
      result.push({
        id: "product",
        label: t("Product"),
      });
    }

    result.push(
      {
        id: "details",
        label: t("Details"),
      },
      {
        id: "contact",
        label: t("Contact"),
      },
      {
        id: "review",
        label: t("Review"),
      },
    );

    return result;
  }, [complaintType, t]);

  const currentStep = steps[activeStep];

  // =========================================================
  // LOAD CONTACT
  // =========================================================

  useEffect(() => {
    if (!open || !token || !userName) {
      return;
    }

    if (currentStep?.id !== "contact") {
      return;
    }

    const loadContact = async () => {
      try {
        const data = await GetInfosContact(token, userName);

        console.log("CONTACT DATA:", data);

        const fullName = `${data?.name ?? ""} ${data?.lastName ?? ""}`.trim();

        setContactName(fullName);

        setPhoneNumber(data?.noTelephonePrincipal ?? "");
      } catch (error) {
        console.error("Unable to load contact:", error);

        setContactName("");
        setPhoneNumber("");
      }
    };

    loadContact();
  }, [open, token, userName, currentStep?.id]);

  // =========================================================
  // TYPE CHANGE
  // =========================================================

  const handleTypeChange = (type) => {
    setComplaintType(type);

    setShowValidation(false);

    // ITEM
    setSelectedItem("");
    setSelectedItemData(null);

    // DETAILS
    setLotNumber("");
    setProductionDate("");
    setExpirationDate("");
    setQuantity("");
    setQuantityType("");
    setDescription("");
    setFiles([]);

    // CONTACT
    setContactName("");
    setPhoneNumber("");
    setDeliveryDate("");
  };

  // =========================================================
  // ITEM CHANGE
  // =========================================================

  const handleSelectedItemChange = (itemId, item) => {
    console.log("SELECTED ITEM ID:", itemId);

    console.log("SELECTED ITEM DATA:", item);

    setSelectedItem(itemId);

    setSelectedItemData(item);

    setShowValidation(false);

    // Reset dynamic fields
    setLotNumber("");
    setProductionDate("");
    setExpirationDate("");
  };

  // =========================================================
  // FILES
  // =========================================================

  const handleFilesChange = (newFiles) => {
    setFiles((previous) => [...previous, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles((previous) => previous.filter((_, i) => i !== index));
  };

  // =========================================================
  // VALIDATE DETAILS
  // =========================================================

  const validateDetails = () => {
    // =======================================================
    // PRODUCT
    // =======================================================

    if (complaintType === "product") {
      // ITEM
      if (!selectedItemData) {
        return false;
      }

      // LOT
      if (selectedItemData?.plainteInfo === 1 && !lotNumber?.trim()) {
        return false;
      }

      // PRODUCTION DATE
      if (selectedItemData?.plainteInfo === 2 && !productionDate) {
        return false;
      }

      // EXPIRATION DATE
      if (selectedItemData?.plainteInfo === 4 && !expirationDate) {
        return false;
      }

      // QUANTITY
      if (!quantity || Number(quantity) <= 0) {
        return false;
      }

      // UNIT / PACK
      if (!quantityType) {
        return false;
      }

      // DESCRIPTION
      if (!description?.trim()) {
        return false;
      }

      // PICTURE
      if (!files || files.length === 0) {
        return false;
      }
    }

    // =======================================================
    // TRANSPORT
    // =======================================================

    if (complaintType === "transport") {
      if (!description?.trim()) {
        return false;
      }
    }

    return true;
  };

  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {
    if (activeStep === 0) {
      return;
    }

    setShowValidation(false);

    setActiveStep((previous) => previous - 1);
  };

  // =========================================================
  // NEXT
  // =========================================================

  const handleNext = () => {
    // =======================================================
    // TYPE
    // =======================================================

    if (currentStep?.id === "type") {
      if (!complaintType) {
        setShowValidation(true);
        return;
      }
    }

    // =======================================================
    // PRODUCT
    // =======================================================

    if (currentStep?.id === "product") {
      if (!selectedItemData) {
        setShowValidation(true);
        return;
      }
    }

    // =======================================================
    // DETAILS
    // =======================================================

    if (currentStep?.id === "details") {
      if (!validateDetails()) {
        setShowValidation(true);
        return;
      }
    }

    // =======================================================
    // CONTACT
    // =======================================================

    if (currentStep?.id === "contact") {
      if (!contactName?.trim() || !phoneNumber?.trim()) {
        setShowValidation(true);
        return;
      }
    }

    // =======================================================
    // VALID
    // =======================================================

    setShowValidation(false);

    // =======================================================
    // NEXT STEP
    // =======================================================

    if (activeStep < steps.length - 1) {
      setActiveStep((previous) => previous + 1);

      return;
    }

    // =======================================================
    // SUBMIT
    // =======================================================

    handleSubmit();
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);

    try {
      // =====================================================
      // TRANSPORT
      // =====================================================

      if (complaintType === "transport") {
        const complaintData = {
          IdRestaurant: idRestaurant,

          NomResponsable: contactName || "",

          PhoneNumber: phoneNumber || "",

          TypePlainte: "Transport",

          Item: "0",

          TextComplaint: description || "",

          Files: files || [],
        };

        console.log("SUBMIT TRANSPORT DATA:", complaintData);

        const result = await SubmitComplaint(token, complaintData);

        console.log("TRANSPORT COMPLAINT SUBMITTED:", result);

        // =================================================
        // INFORMER LE PARENT
        // =================================================

        onComplaintAdded?.();

        // =================================================
        // SUCCESS MESSAGE
        // =================================================

        const message = userLang?.toLowerCase().startsWith("fr")
          ? result?.messageFr
          : result?.messageEn;

        showSnackbar(
          message ||
            (userLang?.toLowerCase().startsWith("fr")
              ? "La plainte de type TRANSPORT a été soumise avec succès."
              : "TRANSPORT Complaint submitted successfully."),
          "success",
        );

        // =================================================
        // CLOSE
        // =================================================

        setTimeout(() => {
          handleClose();
          window.location.reload();
        }, 300);

        return;
      }

      // =====================================================
      // PRODUCT
      // =====================================================

      if (complaintType === "product") {
        const complaintData = {
          IdRestaurant: idRestaurant,

          IdOrder: selectedItemData?.idCommande ?? null,

          Item: selectedItemData?.idItem,

          CustomItem: null,

          BatchNumber: lotNumber || null,

          NomResponsable: contactName || "",

          ProductionDate: productionDate || null,

          ExpirationDate: expirationDate || null,

          PhoneNumber: phoneNumber || null,

          Quantity: Number(quantity) || 0,

          TypeQuantity: quantityType || "",

          TextComplaint: description || "",

          TypePlainte: "Product",

          Files: files || [],

          BatchNumberDisabled: !lotNumber,

          ProductionDateDisabled: !productionDate,

          ExpirationDateDisabled: !expirationDate,
        };

        console.log("SUBMIT PRODUCT DATA:", complaintData);

        const result = await SubmitComplaint(token, complaintData);

        console.log("PRODUCT COMPLAINT SUBMITTED:", result);

        // =================================================
        // INFORMER LE PARENT
        // =================================================

        onComplaintAdded?.();

        // =================================================
        // SUCCESS MESSAGE
        // =================================================

        const message = userLang?.toLowerCase().startsWith("fr")
          ? result?.messageFr
          : result?.messageEn;

        showSnackbar(
          message ||
            (userLang?.toLowerCase().startsWith("fr")
              ? "La plainte de type Product a été soumise avec succès."
              : "Product Complaint submitted successfully."),
          "success",
        );

        // =================================================
        // CLOSE
        // =================================================

        setTimeout(() => {
          handleClose();
          window.location.reload();
        }, 300);

        return;
      }
    } catch (error) {
      console.error("SUBMIT COMPLAINT ERROR:", error);

      // ===================================================
      // ERROR
      // ===================================================

      showSnackbar(
        userLang?.toLowerCase().startsWith("fr")
          ? "Une erreur est survenue lors de l'enregistrement de la plainte."
          : "An error occurred while submitting the complaint.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // =========================================================
  // RESET / CLOSE
  // =========================================================

  const handleClose = () => {
    setActiveStep(0);

    setComplaintType("");

    setSelectedItem("");
    setSelectedItemData(null);

    setLotNumber("");
    setProductionDate("");
    setExpirationDate("");

    setQuantity("");
    setQuantityType("");

    setDescription("");

    setFiles([]);

    setContactName("");
    setPhoneNumber("");
    setDeliveryDate("");

    setShowValidation(false);

    setSubmitting(false);

    onClose();
  };

  // =========================================================
  // RENDER STEP
  // =========================================================

  const renderStep = () => {
    switch (currentStep?.id) {
      // =====================================================
      // TYPE
      // =====================================================

      case "type":
        return (
          <ComplaintTypeStep
            complaintType={complaintType}
            onChange={handleTypeChange}
            showValidation={showValidation}
            t={t}
          />
        );

      // =====================================================
      // PRODUCT
      // =====================================================

      case "product":
        return (
          <ComplaintProductStep
            token={token}
            noEmplacement={noEmplacement}
            selectedItem={selectedItem}
            selectedItemData={selectedItemData}
            onSelectedItemChange={handleSelectedItemChange}
            userLang={userLang}
            showValidation={showValidation}
            t={t}
          />
        );

      // =====================================================
      // DETAILS
      // =====================================================

      case "details":
        return (
          <ComplaintDetailsStep
            complaintType={complaintType}
            selectedItemData={selectedItemData}
            productionDate={productionDate}
            onProductionDateChange={setProductionDate}
            lotNumber={lotNumber}
            onLotNumberChange={setLotNumber}
            expirationDate={expirationDate}
            onExpirationDateChange={setExpirationDate}
            quantity={quantity}
            onQuantityChange={setQuantity}
            quantityType={quantityType}
            onQuantityTypeChange={setQuantityType}
            description={description}
            onDescriptionChange={setDescription}
            files={files}
            onFilesChange={handleFilesChange}
            onRemoveFile={handleRemoveFile}
            showValidation={showValidation}
            t={t}
          />
        );

      // =====================================================
      // CONTACT
      // =====================================================

      case "contact":
        return (
          <ComplaintContactStep
            contactName={contactName}
            phoneNumber={phoneNumber}
            deliveryDate={deliveryDate}
            onContactNameChange={setContactName}
            onPhoneNumberChange={setPhoneNumber}
            showValidation={showValidation}
            t={t}
          />
        );

      // =====================================================
      // REVIEW
      // =====================================================

      case "review":
        return (
          <ComplaintReviewStep
            complaintType={complaintType}
            selectedItem={selectedItem}
            selectedItemData={selectedItemData}
            lotNumber={lotNumber}
            productionDate={productionDate}
            expirationDate={expirationDate}
            quantity={quantity}
            quantityType={quantityType}
            description={description}
            files={files}
            contactName={contactName}
            phoneNumber={phoneNumber}
            userLang={userLang}
            t={t}
          />
        );

      default:
        return null;
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <ComplaintModalLayout
        open={open}
        onClose={handleClose}
        title={t("NewComplaint")}
        subtitle={t("NewComplaintDescription")}
        steps={steps}
        activeStep={activeStep}
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={t("Continue")}
        backLabel={t("Back")}
        isLastStep={activeStep === steps.length - 1}
      >
        {renderStep()}
      </ComplaintModalLayout>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: 350,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ComplaintAddModal;
